import React, { useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { LogOut, Menu, X, LifeBuoy, ShieldCheck, ShieldAlert, BadgeCheck } from 'lucide-react';
import NotificationBell from './NotificationBell';
import PointsBalance from './PointsBalance';
import ThemeToggle from './ThemeToggle';

function VerificationBadge({ user }) {
  if (!user) return null;
  const emailVerified = !!user.is_email_verified;
  const workVerified = !!user.work_email_verified;

  if (emailVerified && workVerified) {
    return (
      <span title="Personal + work email verified" data-testid="verification-badge-full" className="flex-shrink-0 text-blue-600 dark:text-blue-400">
        <BadgeCheck className="w-3.5 h-3.5" />
      </span>
    );
  }
  if (emailVerified) {
    return (
      <span title="Personal email verified" data-testid="verification-badge-partial" className="flex-shrink-0 text-emerald-600 dark:text-emerald-400">
        <ShieldCheck className="w-3.5 h-3.5" />
      </span>
    );
  }
  return (
    <span title="Email not verified" data-testid="verification-badge-none" className="flex-shrink-0 text-slate-400 dark:text-zinc-600">
      <ShieldAlert className="w-3.5 h-3.5" />
    </span>
  );
}

function contactPath(user) {
  const role = user?.active_role || user?.role;
  if (role === 'staffing_head' || role === 'staffing_sub') return '/staffing/contact';
  if (role === 'recruiter') return '/recruiter/contact';
  if (role === 'admin') return '/admin/contact';
  return '/applicant/contact';
}

export default function Sidebar({ items, title }) {
  const { logout, user } = useAuthStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    window.dispatchEvent(new Event('jay:logout'));
    setTimeout(() => { logout(); navigate('/login'); }, 1500);
  };

  const handleSidebarOpen = () => {
    setOpen(true);
    window.dispatchEvent(new Event('jay:sidebar-open'));
  };

  const handleSidebarClose = () => {
    setOpen(false);
    window.dispatchEvent(new Event('jay:sidebar-close'));
  };

  const sidebarContent = (
    <>
      <div className="px-5 py-5 border-b border-slate-200 dark:border-zinc-800/50 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight" data-brand-logo="1" style={{ fontFamily: "'DM Serif Display', serif" }}>
            <span className="text-slate-900 dark:text-white">nu</span><span className="text-emerald-600 dark:text-emerald-400">mu</span><span className="text-slate-900 dark:text-white">v</span>
          </h1>
          <p className="text-xs text-slate-500 dark:text-zinc-500 mt-0.5 capitalize">{title || user?.role} Portal</p>
        </div>
        <div className="flex items-center gap-1">
          <ThemeToggle size="sm" />
          <NotificationBell testIdSuffix="desktop" />
          <button onClick={handleSidebarClose} className="lg:hidden p-1 text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid="close-sidebar">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      <nav className="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto">
        {items.map(({ to, icon: Icon, label }) => (
          <NavLink key={to} to={to} end={to.split('/').length <= 2}
            onClick={handleSidebarClose}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? 'bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white' : 'text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-200 hover:bg-slate-100 dark:hover:bg-zinc-800/50'}`}
            data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <Icon className="w-4 h-4 flex-shrink-0" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-slate-200 dark:border-zinc-800/50">
        <div className="px-3 mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {user?.profile_photo_url ? (
              <img
                src={/^https?:\/\//i.test(user.profile_photo_url) ? user.profile_photo_url : (process.env.REACT_APP_BACKEND_URL + user.profile_photo_url)}
                alt=""
                className="w-7 h-7 rounded-full object-cover border border-slate-300 dark:border-zinc-700 flex-shrink-0"
                data-testid="sidebar-avatar"
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-zinc-800 border border-slate-300 dark:border-zinc-700 flex items-center justify-center text-[11px] font-medium text-slate-600 dark:text-zinc-300 flex-shrink-0" data-testid="sidebar-avatar-fallback">
                {(user?.email || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <p className="text-xs text-slate-500 dark:text-zinc-500 truncate flex-1">{user?.email}</p>
            <VerificationBadge user={user} />
          </div>
          {(user?.active_role === 'recruiter' || user?.active_role === 'admin' || user?.role === 'recruiter' || user?.role === 'admin') && (
            <PointsBalance to={(user?.active_role || user?.role) === 'admin' ? '/admin/wallets' : '/recruiter/wallet'} />
          )}
        </div>
        <button onClick={handleLogout} data-testid="logout-btn" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-500 dark:text-zinc-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/5 w-full transition-colors">
          <LogOut className="w-4 h-4" />
          <span>Sign Out</span>
        </button>
        {user?.role !== 'support_agent' && (
          <Link to={contactPath(user)} data-testid="contact-support-link" className="flex items-center gap-3 px-3 py-2 mt-1 rounded-md text-xs text-slate-500 dark:text-zinc-500 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-500/5 w-full transition-colors">
            <LifeBuoy className="w-4 h-4" />
            <span>Contact Support</span>
          </Link>
        )}
      </div>
    </>
  );

  return (
    <>
      <div className="lg:hidden banner-shiftable fixed top-0 left-0 right-0 z-40 bg-white dark:bg-[#0e0e10] border-b border-slate-200 dark:border-zinc-800/50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={handleSidebarOpen} className="p-1 text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white" data-testid="open-sidebar">
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold tracking-tight" data-brand-logo="1" style={{ fontFamily: "'DM Serif Display', serif" }}>
            <span className="text-slate-900 dark:text-white">nu</span><span className="text-emerald-600 dark:text-emerald-400">mu</span><span className="text-slate-900 dark:text-white">v</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle size="sm" />
          <NotificationBell testIdSuffix="mobile" />
          <p className="text-xs text-slate-500 dark:text-zinc-500 capitalize">{title || user?.role}</p>
        </div>
      </div>
      <aside className="hidden lg:flex w-60 bg-white dark:bg-[#0e0e10] border-r border-slate-200 dark:border-zinc-800/50 flex-col min-h-screen flex-shrink-0" data-testid="sidebar">
        {sidebarContent}
      </aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={handleSidebarClose} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white dark:bg-[#0e0e10] flex flex-col z-10" data-testid="sidebar-mobile">
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}
